import React, { useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

// Material
import { makeStyles } from "@material-ui/core/styles";

// Config
import { getRoute, urls } from "../../config/routes";
import isCaseLocked from "../../utils/isCaseLocked";

// Actions
import {
  deleteMedicalRecord,
  getMedicalRecords,
  getMedicalRecordsItem
} from "../../state/medicalRecords/actions";

// Hooks
import { useAppInsights } from "../../utils/telemetry/AppInsights";
import useHideToWitnesses from "../../hooks/useHideToWitnesses";

// Components
import CaseMediaWhatsNew from "../CaseMediaWhatsNew";
import LoadingSection from "../LoadingSection";
import MediaItemList from "../MediaItemList";

const useStyles = makeStyles(theme => ({
  content: {
    padding: theme.spacing(5),
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    flexWrap: "wrap"
  }
}));

const MedicalRecordsPanel = () => {
  const { caseId } = useParams();
  const { currentCase, data, isDeleting, isLoading, error } = useSelector(
    s => s.medicalRecords
  );
  const { data: caseData } = useSelector(s => s.caseDetail);
  const caseLocked = isCaseLocked(caseData);
  const { trackException } = useAppInsights();
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  useHideToWitnesses(caseData);

  useEffect(() => {
    if ((currentCase !== caseId || !data) && !error && !isLoading)
      dispatch(
        getMedicalRecords({
          caseId,
          onFail: () =>
            trackException(`Failed to get Medical Records at case: ${caseId}`)
        })
      );
  }, [caseId, currentCase, data, dispatch, isLoading, trackException, error]);

  const handleSelect = id => () => {
    history.push(getRoute(urls.medicalRecordDetail, { caseId, itemId: id }));
  };

  const handleDelete = record => dispatch(deleteMedicalRecord(record));
  const handleFetchImage = recordId => () =>
    dispatch(
      getMedicalRecordsItem({
        caseId,
        recordId,
        onFail: () =>
          trackException(
            `Failed to get medical record item: ${recordId} at case: ${caseId}`
          )
      })
    );

  return (
    <>
      {isLoading ? (
        <div className={classes.content}>
          <LoadingSection normalHeight noBackground />
        </div>
      ) : (
        <div className={classes.content}>
          {data && (
            <MediaItemList
              caseId={caseId}
              data={data}
              mediaItemProps={{ isDeleting, showDelete: !caseLocked }}
              onDelete={handleDelete}
              onFetchImage={handleFetchImage}
              onSelect={handleSelect}
              page="medicalRecordsPage"
            />
          )}
        </div>
      )}
      {data && <CaseMediaWhatsNew defaultClosed={data.length > 0} />}
    </>
  );
};

export default MedicalRecordsPanel;
